import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { formatSlotTime, formatOrdinalDate } from "../lib/timeFormat";
import { CheckCircle2, MapPin, Ban } from "lucide-react";

export default function TicketPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/ticket/${id}`);
        setData(r.data);
      } catch { setErr("Ticket not found"); }
    })();
  }, [id]);

  if (err) return <Wrap><div className="text-center text-[#fca5a5] py-20">{err}</div></Wrap>;
  if (!data) return <Wrap><div className="text-center text-[#8a8a8a] py-20">Loading…</div></Wrap>;

  if (data.cancelled) return (
    <Wrap>
      <div className="max-w-md mx-auto p-5" data-testid="ticket-cancelled">
        <div className="text-center mb-6">
          <div className="st-brand text-3xl text-white">SCARE<span className="text-[#dc2626]">TICKETS</span></div>
        </div>
        <div className="st-card p-8 text-center st-fade-up">
          <div className="w-14 h-14 rounded-full bg-[#2a0e0e] border border-[#dc2626]/40 flex items-center justify-center mx-auto mb-4">
            <Ban size={26} className="text-[#fca5a5]" />
          </div>
          <h1 className="text-xl font-bold">Ticket no longer valid</h1>
          <p className="text-sm text-[#bbb] mt-2">
            This order has been {data.status === "refunded" ? "refunded" : "cancelled"} and the ticket can no longer be used for entry.
          </p>
          {data.order_number && <p className="text-xs text-[#8a8a8a] mt-3">Order #{data.order_number}</p>}
          <p className="text-xs text-[#8a8a8a] mt-4">If you believe this is a mistake, please contact {data.attraction?.name || "the organiser"}.</p>
        </div>
      </div>
    </Wrap>
  );

  const { attendee, attraction, event_date, time_slot, order_number, qr_png_data_uri } = data;

  return (
    <Wrap>
      <div className="max-w-md mx-auto p-5">
        <div className="text-center mb-6">
          <div className="st-brand text-3xl text-white">SCARE<span className="text-[#dc2626]">TICKETS</span></div>
        </div>

        <div className="st-card overflow-hidden st-fade-up">
          {/* header */}
          <div className="px-6 py-5 border-b border-[#1a1a1a] bg-[#0c0c0c]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#dc2626]">Admit one</div>
            <div className="text-2xl font-bold mt-1">{attraction.name}</div>
            <div className="text-xs text-[#8a8a8a] flex items-center gap-1 mt-1"><MapPin size={12}/>{attraction.location}</div>
          </div>

          {/* QR — white panel for maximum scanner contrast */}
          <div className="px-6 py-6 flex justify-center">
            <div className="p-4 rounded-2xl bg-white shadow-[0_0_40px_rgba(255,255,255,0.08)]" data-testid="qr-container">
              <img src={qr_png_data_uri} alt="Ticket QR code" className="block w-[260px] h-[260px] max-w-full" />
            </div>
          </div>

          <div className="px-6 pb-6">
            <Detail label="Attendee" value={attendee.name} />
            <Detail label="Ticket" value={attendee.ticket_type_name} />
            <Detail label="Date" value={formatOrdinalDate(event_date.date)} />
            <Detail label="Time" value={time_slot ? formatSlotTime(time_slot.start_time, time_slot.end_time, attraction, "slot") : formatSlotTime(event_date.open_time, event_date.close_time, attraction, "openHours")} />
            <Detail label="Order" value={`#${order_number}`} mono />
          </div>

          {attendee.checked_in && (
            <div className="bg-[#0c1d12] border-t border-[#1b3a25] px-6 py-3 flex items-center gap-2 text-[#86efac] text-sm">
              <CheckCircle2 size={16}/> Checked in {attendee.checked_in_at?.slice(11,16)}
            </div>
          )}
        </div>

        <p className="text-center text-xs text-[#7a7a7a] mt-6">Show this QR at the door. Don&apos;t lose your soul.</p>
      </div>
    </Wrap>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div className="flex justify-between py-2 border-b border-[#161616] last:border-b-0">
      <span className="text-xs text-[#7a7a7a] uppercase tracking-wider">{label}</span>
      <span className={`text-sm font-semibold ${mono ? "st-mono text-[#dc2626]" : ""}`}>{value}</span>
    </div>
  );
}

function Wrap({ children }) {
  return <div className="min-h-screen st-grain bg-[#0a0a0a] py-10">{children}</div>;
}
